import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Por favor ingresa tu correo electrónico");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Si el email existe, recibirás un enlace de recuperación");
    } catch (error) {
      toast.error("Error al procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md card-elevated p-8 text-center"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-navy-900 mb-4">
            ¡Correo Enviado!
          </h1>
          <p className="text-navy-600 mb-6">
            Si el correo <strong>{email}</strong> está registrado, recibirás un enlace para restablecer tu contraseña.
          </p>
          <p className="text-sm text-navy-500 mb-6">
            El enlace expirará en 1 hora.
          </p>
          <Link to="/login">
            <Button className="w-full bg-gold-600 hover:bg-gold-700 text-white">
              Volver al Login
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-50 flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/login" className="flex items-center gap-2 text-navy-600 hover:text-navy-900 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Volver al login
        </Link>

        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-gold-600 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-xl font-bold text-navy-900">Divídelo</span>
        </div>

        <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
          ¿Olvidaste tu contraseña?
        </h1>
        <p className="text-navy-600 mb-8">
          No te preocupes. Ingresa tu correo y te enviaremos un enlace para restablecerla.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-navy-700">Correo electrónico</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12"
                data-testid="forgot-email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-gold-600 hover:bg-gold-700 text-white"
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Enviar enlace de recuperación"
            )}
          </Button>
        </form>

        <p className="text-center text-navy-600 mt-8">
          ¿Recordaste tu contraseña?{" "}
          <Link to="/login" className="text-gold-600 font-semibold hover:text-gold-700">
            Inicia sesión
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
